import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { CommentThread, AuthorKind } from '../../shared/types';
import type { ReviewKind } from '../../shared/progress';
import { ulid } from '../../shared/ulid';
import { getStoredClientName, setStoredClientName } from '../lib/clientName';
import { clientErrorMessage } from '../lib/api';
import Eyebrow from './Eyebrow';
import Button from './Button';
import { showToast } from './Toaster';

interface Props {
  mode: 'admin' | 'client';
  /** The noun the empty state uses: "script" unless this is an interview
   *  brief. Everything else in the sidebar is document-agnostic. */
  kind?: ReviewKind;
  /** Required when mode === 'client'. Used as the localStorage key for
   *  tracking which admin replies the client has already seen, so we can
   *  badge the comment sidebar with "X new replies" on return visits. */
  slug?: string;
  threads: CommentThread[];
  loading: boolean;
  draftAnchor: { from: number; to: number; text: string; stale?: boolean } | null;
  onCancelDraft: () => void;
  onCreateThread: (params: {
    thread_id: string;
    anchor_text: string;
    anchor_from: number;
    anchor_to: number;
    body: string;
    author_name: string;
  }) => Promise<void>;
  onReply: (threadId: string, body: string, author_name: string) => Promise<void>;
  onResolve?: (threadId: string) => Promise<void>;
  onReopen?: (threadId: string) => Promise<void>;
  onActivateThread: (threadId: string | null) => void;
  activeThreadId: string | null;
}

const ADMIN_NAME = 'Sheahan Studios';

type Filter = 'open' | 'all';

const SEEN_REPLIES_KEY_PREFIX = 'hsp_seen_admin_comments_';

export default function CommentSidebar(props: Props) {
  const {
    mode,
    kind = 'script',
    slug,
    threads,
    loading,
    draftAnchor,
    onCancelDraft,
    onCreateThread,
    onReply,
    onResolve,
    onReopen,
    onActivateThread,
    activeThreadId,
  } = props;

  const [filter, setFilter] = useState<Filter>('open');
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  // Track how many admin-authored comments the client has already seen.
  // Only meaningful in client mode and when we know the slug. The badge
  // clears as soon as the client expands any thread (treats engagement
  // with the sidebar as "I've seen what's new").
  const seenKey = mode === 'client' && slug ? `${SEEN_REPLIES_KEY_PREFIX}${slug}` : null;
  const adminCommentCount = useMemo(
    () =>
      threads.reduce(
        (n, t) => n + t.comments.filter((c) => c.author_kind === 'admin').length,
        0,
      ),
    [threads],
  );
  const [seenAdminCount, setSeenAdminCount] = useState<number>(() => {
    if (!seenKey) return 0;
    try {
      const raw = localStorage.getItem(seenKey);
      return raw ? parseInt(raw, 10) || 0 : 0;
    } catch {
      return 0;
    }
  });
  const newReplyCount = Math.max(0, adminCommentCount - seenAdminCount);

  function markRepliesSeen() {
    if (!seenKey) return;
    setSeenAdminCount(adminCommentCount);
    try {
      localStorage.setItem(seenKey, String(adminCommentCount));
    } catch {
      // ignore — badge will simply re-appear on next render until storage works
    }
  }

  async function handleResolve(threadId: string) {
    if (!onResolve) return;
    setResolvingId(threadId);
    // Let the scale-out animation play before the row leaves the list.
    await new Promise((r) => setTimeout(r, 280));
    await onResolve(threadId);
    setResolvingId(null);
  }

  // Sort by anchor position so threads appear next to where they were placed
  // in the script, not by creation time.
  const visibleThreads = useMemo(() => {
    const filtered =
      filter === 'all' ? threads : threads.filter((t) => t.status !== 'resolved');
    return [...filtered].sort((a, b) => {
      if (a.anchor_from !== b.anchor_from) return a.anchor_from - b.anchor_from;
      return a.created_at - b.created_at;
    });
  }, [threads, filter]);

  const counts = useMemo(() => {
    const open = threads.filter((t) => t.status === 'open' || t.status === 'orphaned').length;
    return { open, total: threads.length };
  }, [threads]);

  return (
    <aside
      id="cr-comments"
      tabIndex={-1}
      className={`w-full md:w-[340px] md:shrink-0 md:sticky md:self-start md:overflow-y-auto focus:outline-none ${
        mode === 'admin'
          ? 'md:top-[68px] md:max-h-[calc(100vh-84px)]'
          : 'md:top-20 md:max-h-[calc(100vh-6rem)]'
      }`}
    >
      <div className="bg-card border border-line rounded-md p-5 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Eyebrow accent>// Comments</Eyebrow>
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-faint">
              {counts.open} open · {counts.total}
            </span>
            {newReplyCount > 0 && (
              <button
                type="button"
                onClick={markRepliesSeen}
                className="
                  inline-flex items-center gap-1.5 shrink-0
                  px-2 py-0.5 rounded-full
                  bg-accent text-accent-fg
                  font-mono text-[10px] uppercase tracking-[0.12em] font-medium
                  shadow-flare/40 hover:opacity-90 transition-opacity
                "
                title="Click to dismiss"
              >
                <span className="w-1 h-1 rounded-full bg-accent-fg" aria-hidden />
                {newReplyCount} new repl{newReplyCount === 1 ? 'y' : 'ies'}
              </button>
            )}
          </div>
          <FilterToggle value={filter} onChange={setFilter} />
        </div>

        {/* Draft composer */}
        {draftAnchor && (
          <Composer
            mode={mode}
            anchor={draftAnchor}
            onCancel={onCancelDraft}
            onSubmit={async (body, name) => {
              await onCreateThread({
                thread_id: ulid(),
                anchor_text: draftAnchor.text,
                anchor_from: draftAnchor.from,
                anchor_to: draftAnchor.to,
                body,
                author_name: name,
              });
              onCancelDraft();
            }}
          />
        )}

        {/* List */}
        {loading && threads.length === 0 ? (
          <div className="space-y-2 mt-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-20 rounded-sm skeleton" />
            ))}
          </div>
        ) : visibleThreads.length === 0 && !draftAnchor ? (
          <EmptyState mode={mode} kind={kind} />
        ) : (
          <ul className="space-y-3 mt-2">
            {visibleThreads.map((t) => (
              <ThreadCard
                key={t.id}
                thread={t}
                mode={mode}
                active={activeThreadId === t.id}
                resolving={resolvingId === t.id}
                onActivate={() => {
                  // Engaging with any thread counts as "I've seen what's new."
                  if (newReplyCount > 0) markRepliesSeen();
                  onActivateThread(activeThreadId === t.id ? null : t.id);
                }}
                onReply={onReply}
                onResolve={handleResolve}
                onReopen={onReopen}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function FilterToggle({ value, onChange }: { value: Filter; onChange: (v: Filter) => void }) {
  return (
    <div className="inline-flex rounded-full bg-elevated border border-line p-0.5">
      {(['open', 'all'] as Filter[]).map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] font-medium transition-colors duration-150 ${
            value === opt ? 'bg-fg text-page' : 'text-muted hover:text-fg'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ mode, kind }: { mode: 'admin' | 'client'; kind: ReviewKind }) {
  const noun = kind === 'brief' ? 'brief' : 'script';
  return (
    <div className="border border-dashed border-line rounded-sm px-4 py-8 text-center">
      <p className="text-[13px] text-muted leading-[1.55] max-w-[26ch] mx-auto">
        {mode === 'admin'
          ? `No comments yet. Highlight any text in the ${noun} to start a thread.`
          : `Highlight any line in the ${noun} to leave a comment for the producer.`}
      </p>
    </div>
  );
}

interface ComposerProps {
  mode: 'admin' | 'client';
  anchor: { from: number; to: number; text: string; stale?: boolean };
  onSubmit: (body: string, authorName: string) => Promise<void>;
  onCancel: () => void;
}

function Composer({ mode, anchor, onSubmit, onCancel }: ComposerProps) {
  const [body, setBody] = useState('');
  const [name, setName] = useState(() => (mode === 'client' ? getStoredClientName() : ADMIN_NAME));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Locked at mount so the field doesn't unmount as the user types their first letter.
  const [needsName] = useState(() => mode === 'client' && !getStoredClientName());
  const formRef = useRef<HTMLFormElement | null>(null);

  // On phones the sidebar (and this composer) sits far below the script, so
  // "+ Comment" would otherwise appear to do nothing. Bring the composer
  // into view; desktop keeps its position (sticky sidebar, already visible).
  useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    if (mode === 'client' && !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'client') setStoredClientName(name.trim());
      await onSubmit(body.trim(), mode === 'admin' ? ADMIN_NAME : name.trim());
      setBody('');
      showToast('success', 'Comment posted.');
    } catch (err) {
      // Never fail silently — the client must know their feedback didn't save.
      setError(clientErrorMessage(err, 'That didn’t save. Please try again in a moment.'));
      setBusy(false);
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="border border-accent/60 bg-elevated rounded-sm p-3 mb-4 animate-rise-subtle"
    >
      <div className="border-l-2 border-accent pl-2.5 mb-3 max-h-12 overflow-hidden">
        <p className="text-[12px] text-muted italic line-clamp-2">"{anchor.text}"</p>
      </div>
      {anchor.stale && (
        <p className="text-[12px] text-muted leading-[1.5] -mt-1 mb-3">
          Select the text again to place this comment. Your words are kept.
        </p>
      )}

      {needsName && (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          autoFocus
          className="w-full px-3 py-2 mb-2 bg-card border border-line rounded-sm text-fg text-[13px] focus:border-accent focus:shadow-focus focus:outline-none transition-all duration-150"
        />
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Leave a comment…"
        rows={3}
        autoFocus={!needsName}
        disabled={busy}
        className="w-full px-3 py-2 bg-card border border-line rounded-sm text-fg text-[14px] resize-y focus:border-accent focus:shadow-focus focus:outline-none transition-all duration-150"
      />

      {error && (
        <p role="alert" className="text-[12px] text-accent leading-[1.5] mt-2">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 mt-3">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={busy || !!anchor.stale || !body.trim() || (mode === 'client' && !name.trim())}
        >
          {busy ? 'Posting…' : error ? 'Try again' : 'Post comment'}
        </Button>
      </div>
    </form>
  );
}

interface ThreadCardProps {
  thread: CommentThread;
  mode: 'admin' | 'client';
  active: boolean;
  resolving: boolean;
  onActivate: () => void;
  onReply: (threadId: string, body: string, author_name: string) => Promise<void>;
  onResolve?: (threadId: string) => Promise<void>;
  onReopen?: (threadId: string) => Promise<void>;
}

function ThreadCard({
  thread,
  mode,
  active,
  resolving,
  onActivate,
  onReply,
  onResolve,
  onReopen,
}: ThreadCardProps) {
  const isResolved = thread.status === 'resolved';
  const [replyOpen, setReplyOpen] = useState(false);
  // Two-click resolve guard. First click arms the button (visual change to
  // "Confirm resolve"); a second click within 3s commits. The arm clears on
  // its own after the timeout so a stray click can't be confirmed minutes later.
  const [confirmingResolve, setConfirmingResolve] = useState(false);
  const resolveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (resolveTimer.current) window.clearTimeout(resolveTimer.current);
    };
  }, []);

  function handleResolveClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (!onResolve) return;
    if (confirmingResolve) {
      if (resolveTimer.current) window.clearTimeout(resolveTimer.current);
      setConfirmingResolve(false);
      void onResolve(thread.id);
      return;
    }
    setConfirmingResolve(true);
    resolveTimer.current = window.setTimeout(() => {
      setConfirmingResolve(false);
    }, 3000);
  }

  return (
    <li
      className={resolving ? 'animate-scale-out pointer-events-none' : ''}
    >
      <div
        className={`
          rounded-sm border transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
          ${active ? 'border-accent bg-accent/5 shadow-flare/30' : 'border-line bg-elevated/40'}
          ${isResolved ? 'opacity-60' : ''}
        `}
      >
        <button
          type="button"
          onClick={onActivate}
          className="block w-full text-left p-3 cursor-pointer"
        >
          <div className="border-l-2 border-accent/60 pl-2.5 mb-2 max-h-10 overflow-hidden">
            <p className="text-[12px] text-muted italic line-clamp-2">"{thread.anchor_text}"</p>
            {mode === 'admin' && thread.unpublished_anchor && <p className="text-[11px] text-muted mt-1" role="note">Clients see this comment once its text is published.</p>}
          </div>
          <div className="space-y-2">
            {thread.comments.map((c) => (
              <CommentItem key={c.id} authorName={c.author_name} authorKind={c.author_kind} body={c.body} createdAt={c.created_at} />
            ))}
          </div>
        </button>

        {/* Action row */}
        <div className="flex items-center justify-end gap-1 px-2 pb-2">
          {!isResolved && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setReplyOpen((v) => !v);
              }}
              className="font-mono text-[10px] uppercase tracking-[0.12em] font-medium px-2 py-1 rounded text-muted hover:text-fg transition-colors"
            >
              {replyOpen ? 'Cancel' : 'Reply'}
            </button>
          )}
          {!isResolved && onResolve && (
            <button
              type="button"
              onClick={handleResolveClick}
              className={`font-mono text-[10px] uppercase tracking-[0.12em] font-medium px-2 py-1 rounded transition-colors ${
                confirmingResolve
                  ? 'bg-accent text-page'
                  : 'text-accent hover:bg-accent/10'
              }`}
            >
              {confirmingResolve ? 'Click to confirm' : 'Resolve'}
            </button>
          )}
          {isResolved && onReopen && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void onReopen(thread.id);
              }}
              className="font-mono text-[10px] uppercase tracking-[0.12em] font-medium px-2 py-1 rounded text-muted hover:text-fg transition-colors"
            >
              Reopen
            </button>
          )}
        </div>

        {replyOpen && (
          <ReplyForm
            mode={mode}
            onSubmit={async (body, name) => {
              await onReply(thread.id, body, name);
              setReplyOpen(false);
            }}
          />
        )}
      </div>
    </li>
  );
}

function CommentItem({
  authorName,
  authorKind,
  body,
  createdAt,
}: {
  authorName: string;
  authorKind: AuthorKind;
  body: string;
  createdAt: number;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="font-medium text-[12px] text-fg">{authorName}</span>
        <span
          className={`font-mono text-[9px] uppercase tracking-[0.12em] ${
            authorKind === 'admin' ? 'text-accent' : 'text-faint'
          }`}
        >
          {authorKind === 'admin' ? '· Studio' : '· Client'}
        </span>
        <span className="font-mono text-[10px] text-faint ml-auto">{timeAgo(createdAt)}</span>
      </div>
      <p className="text-[13px] text-muted leading-[1.5] mt-0.5 whitespace-pre-wrap break-words">{body}</p>
    </div>
  );
}

function ReplyForm({
  mode,
  onSubmit,
}: {
  mode: 'admin' | 'client';
  onSubmit: (body: string, name: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [name, setName] = useState(() => (mode === 'client' ? getStoredClientName() : ADMIN_NAME));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsName] = useState(() => mode === 'client' && !getStoredClientName());

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    if (mode === 'client' && !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'client') setStoredClientName(name.trim());
      await onSubmit(body.trim(), mode === 'admin' ? ADMIN_NAME : name.trim());
      showToast('success', 'Reply posted.');
    } catch (err) {
      setError(clientErrorMessage(err, 'That didn’t save. Please try again in a moment.'));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-line/60 px-3 py-3 space-y-2">
      {needsName && (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          autoFocus
          className="w-full px-3 py-1.5 bg-card border border-line rounded-sm text-fg text-[13px] focus:border-accent focus:shadow-focus focus:outline-none"
        />
      )}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Reply…"
        rows={2}
        autoFocus={!needsName}
        disabled={busy}
        className="w-full px-3 py-1.5 bg-card border border-line rounded-sm text-fg text-[13px] resize-y focus:border-accent focus:shadow-focus focus:outline-none transition-all duration-150"
      />
      {error && (
        <p role="alert" className="text-[12px] text-accent leading-[1.5]">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={busy || !body.trim() || (mode === 'client' && !name.trim())}
        >
          {busy ? 'Posting…' : error ? 'Try again' : 'Post reply'}
        </Button>
      </div>
    </form>
  );
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
